import { Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { keys, omit, pick } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { p2pProvidersApi } from 'api';
import {
  EntityDetailsPage,
  EntityDetailsPageChildrenProps,
  FormikNumericField,
  FormikSelect,
  FormikTextField,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import { useCurrencies, useUser, useP2PProvidersConfig } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { P2PProvider, P2PProviderDto } from 'types';

import { P2P_PROVIDER_INITIAL_VALUE } from './constants';
import { P2PProviderPayinConfig } from './P2PProviderPayinConfig';
import { P2PProviderPayoutConfig } from './P2PProviderPayoutConfig';
import { P2PProviderPayoutLimits } from './P2PProviderPayoutLimits';
import { getP2PProviderValidationSchema } from './utils';

export const P2PProviderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = useMemo(() => id === NEW_ID, [id]);

  const {
    assetCurrenciesOptions,
    fiatCurrenciesOptions,
    defaultAssetCurrency,
    getAssetCurrencySymbol,
    getCurrencyExchangeOptions,
    getAssetCurrencyExchangeOptions,
  } = useCurrencies();

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const { isTechOperator, isAdmin } = useUser();

  const title = useMemo(
    () => (isNew ? t('details.create_title') : t('details.edit_title')),
    [t, isNew],
  );

  const listUrl = useMemo(() => {
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.P2P_PROVIDERS;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.P2P_PROVIDERS;
    }
  }, [isAdmin, isTechOperator]);

  const initialValues = useMemo(
    (): P2PProviderDto => P2P_PROVIDER_INITIAL_VALUE,
    [],
  );

  const validationSchema: Yup.ObjectSchema<P2PProviderDto> = useMemo(
    () => getP2PProviderValidationSchema(defaultAssetCurrency?.id),
    [defaultAssetCurrency?.id],
  );

  const handlePayinTradeMethodsChange = useCallback(
    (event: any, formik: FormikProps<P2PProviderDto>) => {
      const newSelectedTradeMethods = event.target.value;

      formik.setFieldValue(
        `payinConfig.tradeMethods`,
        newSelectedTradeMethods.map((id: string) => ({ id })),
      );
    },
    [],
  );

  const handlePayoutTradeMethodsChange = useCallback(
    (event: any, formik: FormikProps<P2PProviderDto>) => {
      const newSelectedTradeMethods = event.target.value;

      formik.setFieldValue(
        `payoutConfig.tradeMethods`,
        newSelectedTradeMethods.map((id: string) => ({ id })),
      );
    },
    [],
  );

  const resetCurrencyRelatedFields = useCallback(
    (formik: FormikProps<P2PProviderDto>) => {
      formik.setFieldValue('currencyExchangeId', '');
      formik.setFieldTouched('currencyExchangeId', true);

      formik.setFieldValue('assetCurrencyExchangeId', '');
      formik.setFieldTouched('assetCurrencyExchangeId', true);
    },
    [],
  );

  const handleFiatCurrencyChange = useCallback(
    (formik: FormikProps<P2PProviderDto>) => {
      formik.setFieldValue('payinConfig.tradeMethods', []);
      formik.setFieldValue('payoutConfig.tradeMethods', []);
      resetCurrencyRelatedFields(formik);
    },
    [resetCurrencyRelatedFields],
  );

  const handleAssetCurrencyChange = useCallback(
    (formik: FormikProps<P2PProviderDto>) => {
      resetCurrencyRelatedFields(formik);
    },
    [resetCurrencyRelatedFields],
  );

  const initialValuesSelect = useCallback(
    (data: P2PProvider) => {
      const pickedData = pick(data, keys(initialValues));
      pickedData.payinConfig = data.payinConfig
        ? pick(data.payinConfig, keys(initialValues.payinConfig))
        : initialValues.payinConfig;
      pickedData.payoutConfig = data.payoutConfig
        ? pick(data.payoutConfig, keys(initialValues.payoutConfig))
        : initialValues.payoutConfig;
      pickedData.payoutLimits = data.payoutLimits
        ? pick(data.payoutLimits, keys(initialValues.payoutLimits))
        : initialValues.payoutLimits;
      return pickedData;
    },
    [initialValues],
  );

  const formatCurrencySymbol = useCallback(
    (assetCurrencyId?: string) =>
      assetCurrencyId
        ? getAssetCurrencySymbol(assetCurrencyId)
        : defaultAssetCurrency?.symbol,
    [defaultAssetCurrency?.symbol, getAssetCurrencySymbol],
  );

  const onBeforeUpdateMutation = useCallback(
    ({
      id,
      data,
    }: {
      id: string;
      data: P2PProviderDto;
    }): { id: string; data: P2PProviderDto } => ({
      id,
      data: omit(data, ['type', 'assetCurrencyId']),
    }),
    [],
  );

  const { availableP2PProviderTypeOptions } = useP2PProvidersConfig();

  return (
    <EntityDetailsPage
      title={title}
      id={id!}
      api={p2pProvidersApi}
      apiAsRole={false}
      listUrl={listUrl}
      initialValues={initialValues}
      validationSchema={validationSchema}
      queryKey={QueryKey.P2PProviders}
      formClassName="tw-max-w-md"
      initialValuesSelect={initialValuesSelect}
      onBeforeUpdateMutation={onBeforeUpdateMutation}
    >
      {({
        formik,
        queryResult,
      }: EntityDetailsPageChildrenProps<P2PProviderDto, P2PProvider>) => (
        <Fragment>
          <Typography variant="h5">{t('details.common_details')}</Typography>
          <FormikTextField label={t('fields.name')} name="name" required />
          <FormikSelect
            label={t('fields.type')}
            name="type"
            options={availableP2PProviderTypeOptions}
            disabled={!!queryResult.data}
            required
          />
          <FormikNumericField
            label={t('fields.withdrawal_fee')}
            allowNegative={false}
            decimalScale={0}
            name="withdrawalFee"
            suffix={formatCurrencySymbol(formik.values.assetCurrencyId)}
            fullWidth
            required
          />
          <FormikNumericField
            label={t('fields.max_balance')}
            allowNegative={false}
            decimalScale={0}
            defaultValue={0}
            name="maxBalance"
            suffix={formatCurrencySymbol(formik.values.assetCurrencyId)}
            fullWidth
          />
          <FormikSelect
            label={t('fields.fiatCurrencyId')}
            name="fiatCurrencyId"
            options={fiatCurrenciesOptions}
            onChange={() => handleFiatCurrencyChange(formik)}
            required
          />
          <FormikSelect
            label={t('fields.assetCurrencyId')}
            name="assetCurrencyId"
            options={assetCurrenciesOptions}
            onChange={() => handleAssetCurrencyChange(formik)}
            disabled={!!queryResult.data}
            required
          />
          {formik.values.assetCurrencyId && (
            <FormikSelect
              label={t('fields.currency_exchange')}
              helperText={t('fields.currency_exchange_description')}
              name="currencyExchangeId"
              required={
                !!formik.values.assetCurrencyId &&
                defaultAssetCurrency?.id !== formik.values.assetCurrencyId
              }
              noneOption
              options={getCurrencyExchangeOptions({
                fiatCurrencyId: formik.values.fiatCurrencyId,
                assetCurrencyId: formik.values.assetCurrencyId,
              })}
            />
          )}

          {formik.values.assetCurrencyId &&
            formik.values.assetCurrencyId !== defaultAssetCurrency?.id && (
              <FormikSelect
                label={t('fields.asset_currency_exchange')}
                name="assetCurrencyExchangeId"
                required
                helperText={t(
                  'fields.withdrawal_currency_exchange_description',
                )}
                options={getAssetCurrencyExchangeOptions(
                  formik.values.assetCurrencyId,
                )}
                noneOption
              />
            )}

          <P2PProviderPayinConfig
            providerType={formik.values.type}
            assetCurrencyId={formik.values.assetCurrencyId}
            fiatCurrencyId={formik.values.fiatCurrencyId}
            onTradeMethodsChange={(event) =>
              handlePayinTradeMethodsChange(event, formik)
            }
            isNew={isNew}
          />

          <P2PProviderPayoutConfig
            providerType={formik.values.type}
            assetCurrencyId={formik.values.assetCurrencyId}
            fiatCurrencyId={formik.values.fiatCurrencyId}
            onTradeMethodsChange={(event) =>
              handlePayoutTradeMethodsChange(event, formik)
            }
            isNew={isNew}
          />

          <P2PProviderPayoutLimits />
        </Fragment>
      )}
    </EntityDetailsPage>
  );
};
